/**
 * Recovery Trauma™ — Expanded Journal Prompt Library
 *
 * Editorial rules (trauma-informed):
 *  - Invitational, curious language. No commands, no diagnosis, no "you must".
 *  - Never asks for graphic re-telling of traumatic events.
 *  - Never instructs forgiveness, confrontation, or blame.
 *  - Journalling is reflection — never presented as therapy or a cure.
 *
 * This file only ADDS discovery data. Existing entries, prompts, favourites
 * and weekly reviews in `journal-prompts.ts` are untouched.
 */

export type PromptDepth = "gentle" | "deeper" | "deep-dive";

export interface LibraryPrompt {
  id: string;
  text: string;
  depth: PromptDepth;
  /** Backend tags — power search, feelings and focus filters. */
  tags: string[];
}

export const DEPTH_LABELS: Record<PromptDepth, string> = {
  gentle: "Gentle",
  deeper: "Deeper",
  "deep-dive": "Deep Dive",
};

export const DEPTH_NOTE =
  "This prompt may bring up difficult emotions. You can stop, pause or choose something gentler at any time.";

/* ------------------------------------------------------------------ */
/* Prompts                                                             */
/* ------------------------------------------------------------------ */

type Raw = [text: string, depth: PromptDepth, tags: string[]];

const RAW: Raw[] = [
  // --- Trauma recovery / C-PTSD / progress -------------------------
  ["What has felt even slightly easier this month than it did six months ago?", "gentle", ["trauma-recovery", "recovery-progress", "hope"]],
  ["What have you survived that you rarely give yourself credit for?", "deeper", ["trauma-recovery", "strengths", "self-worth", "recovery-progress"]],
  ["What does recovery mean to you today — in your own words, not anyone else's?", "gentle", ["trauma-recovery", "values", "identity"]],
  ["Where are you expecting yourself to heal on a schedule?", "deeper", ["trauma-recovery", "perfectionism", "self-compassion"]],
  ["What part of your recovery feels stuck, and what might that part be protecting?", "deep-dive", ["trauma-recovery", "cptsd", "personal-growth"]],
  ["What is one thing your nervous system needs from you today?", "gentle", ["nervous-system", "needs", "body-awareness"]],
  ["Which coping strategy kept you alive once but costs you something now?", "deep-dive", ["survival-mode", "cptsd", "personal-growth"]],
  ["What are you still blaming yourself for that may have been a survival response?", "deep-dive", ["shame", "guilt", "survival-mode", "self-compassion", "cptsd"]],
  ["What does 'healing' look like on an ordinary, unremarkable day?", "gentle", ["trauma-recovery", "recovery-progress"]],
  ["Which small habit is quietly holding you together right now?", "gentle", ["recovery-progress", "strengths", "self-compassion"]],

  // --- Emotional flashbacks / triggers ------------------------------
  ["What happened today that made your nervous system feel unsafe, even if you logically knew you were safe?", "deeper", ["triggers", "nervous-system", "safety", "hypervigilance"]],
  ["When a feeling arrives much bigger than the moment, how old do you feel inside?", "deep-dive", ["emotional-flashbacks", "inner-child", "cptsd", "triggers"]],
  ["What helps you notice the difference between 'then' and 'now'?", "gentle", ["emotional-flashbacks", "safety", "emotional-regulation"]],
  ["What were the first signs today that you were becoming activated?", "gentle", ["triggers", "body-awareness", "hypervigilance"]],
  ["What tends to help you come back to yourself after a hard wave passes?", "gentle", ["emotional-flashbacks", "emotional-regulation", "nervous-system"]],
  ["Which everyday situations ask the most from your nervous system?", "deeper", ["triggers", "nervous-system", "overwhelm"]],
  ["What would you like someone to know about your triggers — even if you never tell them?", "deeper", ["triggers", "relationships", "needs"]],
  ["What is a kind sentence you could say to yourself in the middle of a flashback?", "gentle", ["emotional-flashbacks", "self-compassion", "emotional-regulation"]],

  // --- Anxiety / overthinking / overwhelm ---------------------------
  ["What is your anxiety trying to protect you from right now?", "deeper", ["anxiety", "fear", "nervous-system"]],
  ["What are you rehearsing in your mind that hasn't happened yet?", "deeper", ["overthinking", "anxiety", "control"]],
  ["Which worry keeps returning, and what would it need in order to rest a little?", "deeper", ["anxiety", "overthinking", "needs"]],
  ["Where does anxiety live in your body today?", "gentle", ["anxiety", "body-awareness", "nervous-system"]],
  ["If your to-do list could be halved, what would you keep?", "gentle", ["overwhelm", "needs", "boundaries"]],
  ["What feels like too much right now, and what is one piece of it you could set down?", "gentle", ["overwhelm", "emotional-regulation", "boundaries"]],
  ["What are you replaying from a conversation, and what are you hoping to solve by replaying it?", "deeper", ["overthinking", "anxiety", "relationships"]],
  ["What does 'enough for today' look like?", "gentle", ["overwhelm", "perfectionism", "self-compassion"]],
  ["What decision are you avoiding, and what feels risky about making it?", "deeper", ["overthinking", "fear", "control"]],
  ["When your mind races, what has helped it slow even slightly?", "gentle", ["overthinking", "emotional-regulation", "nervous-system"]],

  // --- Anger / resentment -------------------------------------------
  ["What is your anger pointing towards that matters to you?", "deeper", ["anger", "values", "needs"]],
  ["Where were you not allowed to be angry growing up?", "deep-dive", ["anger", "childhood-trauma", "family-trauma", "emotional-neglect"]],
  ["What boundary might your irritation be trying to name?", "deeper", ["anger", "boundaries", "needs"]],
  ["What resentment are you carrying, and what did you give that was never acknowledged?", "deep-dive", ["resentment", "anger", "relationships", "people-pleasing"]],
  ["How does anger feel in your body before you have words for it?", "gentle", ["anger", "body-awareness"]],
  ["What would you say if you knew it would be received without defensiveness?", "deeper", ["anger", "relationships", "needs"]],

  // --- Sadness / grief / loss ---------------------------------------
  ["What are you grieving that wasn't a person — a childhood, relationship, future, opportunity or version of yourself?", "deep-dive", ["grief", "sadness", "letting-go", "identity"]],
  ["What does your sadness want you to notice rather than fix?", "gentle", ["sadness", "self-compassion"]],
  ["What do you miss that you rarely let yourself miss out loud?", "deeper", ["grief", "loneliness", "sadness"]],
  ["What has grief changed about how you see time?", "deeper", ["grief", "identity", "personal-growth"]],
  ["Where does your sadness feel heaviest today, and what would comfort look like?", "gentle", ["sadness", "body-awareness", "needs"]],
  ["What would you like to keep from what you have lost?", "deeper", ["grief", "letting-go", "values"]],
  ["What kind of support do you wish people offered when you're grieving?", "gentle", ["grief", "needs", "relationships"]],

  // --- Shame / guilt -------------------------------------------------
  ["What story do you tell yourself when shame arrives — and where did you first learn it?", "deep-dive", ["shame", "limiting-beliefs", "childhood-trauma"]],
  ["What would change if you separated what happened to you from who you are?", "deep-dive", ["shame", "identity", "self-worth"]],
  ["What are you apologising for that was never yours to carry?", "deeper", ["guilt", "shame", "people-pleasing"]],
  ["Where does shame make you want to hide, and what might it be trying to keep safe?", "deeper", ["shame", "safety", "self-compassion"]],
  ["What is one thing you could forgive yourself for — at your own pace, no pressure?", "deeper", ["guilt", "self-compassion", "forgiveness"]],
  ["Who benefits from you feeling guilty for having needs?", "deeper", ["guilt", "needs", "boundaries", "codependency"]],

  // --- Fear / safety -------------------------------------------------
  ["What does safety feel like in your body — not just in your thoughts?", "gentle", ["safety", "body-awareness", "nervous-system"]],
  ["What are you afraid might happen if you let yourself relax?", "deeper", ["fear", "hypervigilance", "safety"]],
  ["Where in your life do you feel most at ease, and what makes that possible?", "gentle", ["safety", "nervous-system", "hope"]],
  ["What are you scanning for when you walk into a room?", "deeper", ["hypervigilance", "safety", "fear"]],
  ["What would 'safe enough' look like today — not perfectly safe, just safe enough?", "gentle", ["safety", "self-compassion", "nervous-system"]],
  ["What fear feels older than your current life?", "deep-dive", ["fear", "childhood-trauma", "emotional-flashbacks"]],

  // --- Loneliness / numbness / dissociation --------------------------
  ["When did you last feel truly seen, and what made that possible?", "deeper", ["loneliness", "relationships", "trust"]],
  ["What kind of company would feel nourishing right now, rather than draining?", "gentle", ["loneliness", "needs", "boundaries"]],
  ["What makes it hard to reach out, even when you want connection?", "deeper", ["loneliness", "attachment", "fear"]],
  ["When you feel numb, what tends to come back online first?", "gentle", ["numbness", "dissociation", "body-awareness"]],
  ["What might your numbness be giving you a break from?", "deeper", ["numbness", "dissociation", "survival-mode"]],
  ["What small sensory thing helps you feel a little more here?", "gentle", ["dissociation", "nervous-system", "emotional-regulation"]],
  ["When did you notice yourself drifting today, and what came just before it?", "deeper", ["dissociation", "triggers", "body-awareness"]],
  ["What does loneliness feel like in your body, separate from the story about it?", "deeper", ["loneliness", "body-awareness", "sadness"]],

  // --- Emotional regulation / nervous system / body ------------------
  ["Which emotion do you find hardest to allow, and what happens when it shows up?", "deeper", ["emotional-regulation", "identity", "self-trust"]],
  ["What does your body do when it feels overwhelmed — and what does it do when it feels settled?", "gentle", ["nervous-system", "body-awareness", "overwhelm"]],
  ["What regulates you that costs nothing?", "gentle", ["emotional-regulation", "nervous-system", "strengths"]],
  ["Where do you hold tension most often, and what might that part be bracing for?", "deeper", ["body-awareness", "hypervigilance", "nervous-system"]],
  ["What does your body ask for that you routinely postpone?", "deeper", ["body-awareness", "needs", "self-trust"]],
  ["What is the difference for you between numbing and resting?", "deeper", ["numbness", "needs", "emotional-regulation"]],
  ["What helps you come down after a difficult day?", "gentle", ["emotional-regulation", "nervous-system", "needs"]],
  ["Which of the four responses — fight, flight, freeze or fawn — feels most familiar to you lately?", "deeper", ["fight-flight-freeze-fawn", "survival-mode", "nervous-system"]],
  ["When you freeze, what would you want someone to understand about that moment?", "deeper", ["fight-flight-freeze-fawn", "needs", "relationships"]],
  ["What does fawning look like for you in ordinary conversations?", "deeper", ["fight-flight-freeze-fawn", "people-pleasing", "boundaries"]],

  // --- Survival mode / hypervigilance / control ----------------------
  ["What are you trying to control because uncertainty feels unsafe?", "deeper", ["control", "fear", "safety", "survival-mode"]],
  ["Where are you still living as if the emergency hasn't ended?", "deep-dive", ["survival-mode", "hypervigilance", "cptsd"]],
  ["What would you do with your energy if you weren't bracing?", "deeper", ["hypervigilance", "hope", "future-self"]],
  ["What does rest feel like in your body — welcome, or unfamiliar?", "gentle", ["survival-mode", "body-awareness", "safety"]],
  ["What are you over-preparing for, and what would 'good enough preparation' look like?", "deeper", ["control", "perfectionism", "anxiety"]],
  ["Whose comfort do you monitor most closely, and what does that cost you?", "deep-dive", ["hypervigilance", "people-pleasing", "codependency"]],

  // --- Self-compassion / worth / esteem / trust ----------------------
  ["What would change if you stopped measuring your worth by how other people treat you?", "deep-dive", ["self-worth", "relationships", "limiting-beliefs"]],
  ["What would you say to a friend in your exact situation?", "gentle", ["self-compassion", "self-worth"]],
  ["What do you like about yourself that has nothing to do with being useful?", "gentle", ["self-worth", "self-esteem", "identity"]],
  ["Where are you still trying to earn something you already deserve?", "deeper", ["self-worth", "people-pleasing", "limiting-beliefs"]],
  ["When did you last trust your own read on a situation, and how did it go?", "deeper", ["self-trust", "identity", "recovery-progress"]],
  ["What makes it hard to trust yourself, and where did that doubt begin?", "deep-dive", ["self-trust", "childhood-trauma", "limiting-beliefs"]],
  ["What is one promise to yourself you have kept?", "gentle", ["self-trust", "recovery-progress", "strengths"]],
  ["Which compliment is hardest for you to accept, and what does that reveal?", "deeper", ["self-esteem", "limiting-beliefs", "shame"]],
  ["What does your inner critic say when you make a mistake? Whose voice does it remind you of?", "deep-dive", ["inner-critic", "childhood-trauma", "shame"]],
  ["If your inner critic had a job description, what would it think it was protecting you from?", "deeper", ["inner-critic", "fear", "self-compassion"]],
  ["What would a fair, non-punishing version of accountability look like for you?", "deeper", ["guilt", "inner-critic", "self-compassion"]],
  ["Which belief about yourself would you like to hold more loosely?", "deeper", ["limiting-beliefs", "identity", "personal-growth"]],

  // --- Perfectionism -------------------------------------------------
  ["What are you afraid people would think if they saw the unedited version of you?", "deeper", ["perfectionism", "shame", "identity"]],
  ["Where is 'doing it perfectly' actually 'staying safe'?", "deeper", ["perfectionism", "safety", "survival-mode"]],
  ["What could you deliberately do at 80% this week?", "gentle", ["perfectionism", "self-compassion", "personal-growth"]],

  // --- Inner child / childhood / neglect / family --------------------
  ["What did you need to hear as a child that nobody said to you?", "deep-dive", ["inner-child", "childhood-trauma", "emotional-neglect"]],
  ["What part of yourself did you have to hide to feel accepted growing up?", "deep-dive", ["inner-child", "childhood-trauma", "identity", "family-trauma"]],
  ["What brought you joy as a child that you could revisit gently?", "gentle", ["inner-child", "joy", "reconnecting"]],
  ["What would you want your younger self to know about today?", "gentle", ["inner-child", "hope", "self-compassion"]],
  ["Which family rule did nobody say out loud, but everyone followed?", "deep-dive", ["family-trauma", "childhood-trauma", "emotional-neglect"]],
  ["What emotions were welcome in your home, and which ones weren't?", "deep-dive", ["emotional-neglect", "family-trauma", "emotional-regulation"]],
  ["Who did you have to become to keep the peace at home?", "deep-dive", ["family-trauma", "people-pleasing", "identity"]],
  ["What did care look like in your family, and what was missing from it?", "deep-dive", ["emotional-neglect", "childhood-trauma", "needs"]],
  ["What do you find hard to ask for now that was unavailable to you then?", "deeper", ["emotional-neglect", "needs", "attachment"]],
  ["Which role did you play in your family, and do you still play it?", "deeper", ["family-trauma", "identity", "codependency"]],
  ["What does 'being a good child' still mean to you, and is that definition serving you?", "deeper", ["childhood-trauma", "limiting-beliefs", "people-pleasing"]],
  ["What would you want a child to receive that you didn't?", "deeper", ["inner-child", "emotional-neglect", "self-compassion"]],
  ["Where do you notice a younger part of you showing up in adult situations?", "deeper", ["inner-child", "emotional-flashbacks", "relationships"]],

  // --- Attachment / abandonment --------------------------------------
  ["What are you afraid will happen when someone you care about becomes distant?", "deep-dive", ["abandonment", "attachment", "fear", "relationships"]],
  ["What are you afraid might happen when someone pulls away from you?", "deep-dive", ["abandonment", "attachment", "relationships", "fear", "triggers"]],
  ["When you feel abandoned, how old do you feel emotionally?", "deep-dive", ["abandonment", "inner-child", "emotional-flashbacks"]],
  ["What helps you self-soothe when a message goes unanswered?", "gentle", ["abandonment", "anxiety", "emotional-regulation"]],
  ["What do you tend to do when you sense distance — pursue, withdraw, or something else?", "deeper", ["attachment", "relationships", "abandonment"]],
  ["What would secure feel like to you, in ordinary daily terms?", "deeper", ["attachment", "healthy-relationships", "safety"]],
  ["Where did you learn that closeness might not be reliable?", "deep-dive", ["attachment", "childhood-trauma", "trust"]],
  ["What reassurance do you look for most often, and what would it mean to offer some of it to yourself?", "deeper", ["attachment", "abandonment", "self-trust"]],

  // --- Relationships / healthy love -----------------------------------
  ["What behaviour have you previously mistaken for love because it felt familiar?", "deep-dive", ["trauma-bonds", "relationships", "attachment", "childhood-trauma"]],
  ["What does healthy love look like when intensity is removed from the equation?", "deeper", ["healthy-relationships", "trauma-bonds", "relationships"]],
  ["What do you tolerate in relationships because being alone feels more frightening?", "deep-dive", ["relationships", "loneliness", "trauma-bonds", "self-worth"]],
  ["Which red flags have you previously explained away because you wanted the relationship to work?", "deep-dive", ["relationships", "trauma-bonds", "self-trust", "betrayal"]],
  ["Who in your life leaves you feeling more like yourself?", "gentle", ["healthy-relationships", "identity", "relationships"]],
  ["What do you need in a relationship that you've never asked for directly?", "deeper", ["needs", "relationships", "boundaries"]],
  ["What does repair look like to you after conflict?", "deeper", ["healthy-relationships", "relationships", "trust"]],
  ["Where are you the only one adjusting?", "deeper", ["relationships", "people-pleasing", "codependency"]],
  ["What does it feel like in your body to be around someone safe?", "gentle", ["healthy-relationships", "safety", "body-awareness"]],
  ["Which relationship pattern keeps repeating, and what part of it feels familiar?", "deep-dive", ["relationships", "attachment", "trauma-bonds"]],

  // --- Trauma bonds / breakups / letting go ---------------------------
  ["What did the good moments in that relationship give you that you're still looking for?", "deep-dive", ["trauma-bonds", "breakups", "attachment"]],
  ["What are you still hoping will change, and what would it mean if it didn't?", "deep-dive", ["breakups", "letting-go", "trauma-bonds"]],
  ["What do you miss — the person, or how you hoped things would be?", "deep-dive", ["breakups", "grief", "letting-go"]],
  ["What has been quietly better since that relationship ended?", "deeper", ["breakups", "recovery-progress", "hope"]],
  ["What would you want to say to your past self at the start of that relationship?", "deeper", ["breakups", "self-compassion", "trauma-bonds"]],
  ["What are you ready to stop explaining to someone who won't hear it?", "deeper", ["letting-go", "boundaries", "resentment"]],
  ["What are you holding onto that is holding you?", "deeper", ["letting-go", "grief", "personal-growth"]],
  ["What would 'moving on' look like if it didn't require forgetting?", "deeper", ["letting-go", "grief", "breakups"]],
  ["What made leaving hard, and what does that tell you about what you needed?", "deep-dive", ["trauma-bonds", "breakups", "needs"]],

  // --- Boundaries / people-pleasing / codependency --------------------
  ["What boundary do you keep explaining instead of simply allowing yourself to have?", "deeper", ["boundaries", "people-pleasing", "self-worth"]],
  ["What happens inside you when somebody says no to you?", "deeper", ["boundaries", "abandonment", "emotional-regulation"]],
  ["What happens inside you when YOU say no to somebody else?", "deeper", ["boundaries", "guilt", "people-pleasing"]],
  ["Which yes are you giving that is really a no?", "deeper", ["boundaries", "people-pleasing", "self-trust"]],
  ["Where do you over-explain, and what are you hoping to prevent?", "deeper", ["people-pleasing", "boundaries", "fear"]],
  ["Whose emotions do you carry that aren't yours to carry?", "deeper", ["codependency", "boundaries", "family-trauma"]],
  ["What does being liked cost you?", "deep-dive", ["people-pleasing", "self-worth", "identity"]],
  ["What would you do differently today if you weren't managing anyone else's reaction?", "deeper", ["codependency", "boundaries", "needs"]],
  ["What is one small boundary you could keep this week without announcing it?", "gentle", ["boundaries", "self-trust", "personal-growth"]],
  ["What does support look like when it isn't rescuing?", "deeper", ["codependency", "healthy-relationships", "boundaries"]],

  // --- Trust / betrayal / forgiveness ----------------------------------
  ["What would someone need to do consistently for trust to grow with you?", "deeper", ["trust", "healthy-relationships", "safety"]],
  ["Where did you learn to doubt what you were seeing?", "deep-dive", ["trust", "betrayal", "self-trust"]],
  ["What did betrayal teach you about yourself that isn't actually true?", "deep-dive", ["betrayal", "limiting-beliefs", "self-worth"]],
  ["What would you need in order to feel safe being open again — at your own pace?", "deeper", ["trust", "safety", "relationships"]],
  ["What does forgiveness mean to you — and what does it not have to mean?", "deep-dive", ["forgiveness", "resentment", "letting-go"]],
  ["Where are you being asked to 'move on' before you're ready?", "deeper", ["forgiveness", "grief", "boundaries"]],

  // --- Identity / values / strengths / needs ---------------------------
  ["Who are you when nobody needs anything from you?", "deeper", ["identity", "reconnecting", "needs"]],
  ["Which parts of your personality were shaped by protecting yourself?", "deep-dive", ["identity", "survival-mode", "cptsd"]],
  ["What matters to you that you've stopped making room for?", "deeper", ["values", "reconnecting", "needs"]],
  ["Which three values would you like to guide the next month?", "gentle", ["values", "future-self", "personal-growth"]],
  ["What are you good at that you dismiss as 'nothing special'?", "gentle", ["strengths", "self-esteem"]],
  ["What need have you been treating as optional?", "deeper", ["needs", "self-worth", "boundaries"]],
  ["What do you want more of, and what do you want less of?", "gentle", ["needs", "values", "personal-growth"]],
  ["Which version of yourself are you still grieving?", "deep-dive", ["identity", "grief", "letting-go"]],

  // --- Gratitude / joy / hope / future self -----------------------------
  ["What is one small thing that felt good today, however briefly?", "gentle", ["gratitude", "joy"]],
  ["Where did you notice beauty this week?", "gentle", ["joy", "gratitude", "reconnecting"]],
  ["What used to bring you joy that you'd like to try again — with no pressure?", "gentle", ["joy", "reconnecting", "hope"]],
  ["Who or what are you quietly grateful for right now?", "gentle", ["gratitude", "relationships"]],
  ["What are you hopeful about, even cautiously?", "gentle", ["hope", "future-self"]],
  ["What would your future self thank you for starting this month?", "deeper", ["future-self", "personal-growth", "hope"]],
  ["If life felt 10% safer, what would you allow yourself to want?", "deeper", ["future-self", "safety", "hope"]],
  ["What does a good ordinary day look like for you?", "gentle", ["future-self", "values", "hope"]],
  ["What laughter, however small, found you recently?", "gentle", ["joy", "reconnecting"]],

  // --- Personal growth / reconnecting ------------------------------------
  ["What are you doing now that would have surprised you two years ago?", "gentle", ["recovery-progress", "personal-growth", "strengths"]],
  ["What feedback about yourself are you slowly starting to believe?", "deeper", ["personal-growth", "self-worth"]],
  ["What have you outgrown but not yet put down?", "deeper", ["letting-go", "personal-growth", "identity"]],
  ["How do you know when you're being kind to yourself rather than avoiding something?", "deeper", ["self-compassion", "self-trust", "personal-growth"]],
  ["What is one way you could reconnect with your body this week — gently?", "gentle", ["reconnecting", "body-awareness", "nervous-system"]],
  ["What conversation with yourself is overdue?", "deeper", ["reconnecting", "identity", "self-trust"]],
  ["What would tenderness towards yourself look like in practice today?", "gentle", ["self-compassion", "reconnecting"]],
];

export const LIBRARY_PROMPTS: LibraryPrompt[] = RAW.map(([text, depth, tags], i) => ({
  id: `lp-${i + 1}`,
  text,
  depth,
  tags,
}));

/** Featured starter prompt for the "I don't know what to write" card. */
export const STARTER_PROMPT: LibraryPrompt = LIBRARY_PROMPTS.find(
  (p) => p.text === "What are you apologising for that was never yours to carry?",
) ?? LIBRARY_PROMPTS[0];

/* ------------------------------------------------------------------ */
/* Categories                                                          */
/* ------------------------------------------------------------------ */

export interface PromptTagCategory {
  tag: string;
  label: string;
}

export const PROMPT_TAG_CATEGORIES: PromptTagCategory[] = [
  { tag: "trauma-recovery", label: "Trauma Recovery" },
  { tag: "cptsd", label: "C-PTSD" },
  { tag: "emotional-flashbacks", label: "Emotional Flashbacks" },
  { tag: "triggers", label: "Triggers" },
  { tag: "anxiety", label: "Anxiety" },
  { tag: "overthinking", label: "Overthinking" },
  { tag: "overwhelm", label: "Feeling Overwhelmed" },
  { tag: "anger", label: "Anger" },
  { tag: "sadness", label: "Sadness" },
  { tag: "grief", label: "Grief & Loss" },
  { tag: "shame", label: "Shame" },
  { tag: "guilt", label: "Guilt" },
  { tag: "fear", label: "Fear" },
  { tag: "loneliness", label: "Loneliness" },
  { tag: "numbness", label: "Feeling Numb" },
  { tag: "dissociation", label: "Dissociation" },
  { tag: "emotional-regulation", label: "Emotional Regulation" },
  { tag: "nervous-system", label: "Nervous System" },
  { tag: "safety", label: "Safety" },
  { tag: "self-compassion", label: "Self-Compassion" },
  { tag: "self-worth", label: "Self-Worth" },
  { tag: "self-esteem", label: "Self-Esteem" },
  { tag: "self-trust", label: "Self-Trust" },
  { tag: "identity", label: "Identity" },
  { tag: "inner-child", label: "Inner Child" },
  { tag: "childhood-trauma", label: "Childhood Trauma" },
  { tag: "emotional-neglect", label: "Emotional Neglect" },
  { tag: "family-trauma", label: "Family Trauma" },
  { tag: "attachment", label: "Attachment" },
  { tag: "abandonment", label: "Fear of Abandonment" },
  { tag: "relationships", label: "Relationships" },
  { tag: "trauma-bonds", label: "Trauma Bonds" },
  { tag: "breakups", label: "Breakups" },
  { tag: "letting-go", label: "Letting Go" },
  { tag: "boundaries", label: "Boundaries" },
  { tag: "people-pleasing", label: "People-Pleasing" },
  { tag: "codependency", label: "Codependency" },
  { tag: "trust", label: "Trust" },
  { tag: "betrayal", label: "Betrayal" },
  { tag: "forgiveness", label: "Forgiveness" },
  { tag: "resentment", label: "Anger & Resentment" },
  { tag: "inner-critic", label: "Inner Critic" },
  { tag: "limiting-beliefs", label: "Limiting Beliefs" },
  { tag: "control", label: "Control" },
  { tag: "perfectionism", label: "Perfectionism" },
  { tag: "survival-mode", label: "Survival Mode" },
  { tag: "hypervigilance", label: "Hypervigilance" },
  { tag: "fight-flight-freeze-fawn", label: "Fight / Flight / Freeze / Fawn" },
  { tag: "body-awareness", label: "Body Awareness" },
  { tag: "needs", label: "Needs" },
  { tag: "values", label: "Values" },
  { tag: "strengths", label: "Strengths" },
  { tag: "gratitude", label: "Gratitude" },
  { tag: "joy", label: "Joy" },
  { tag: "hope", label: "Hope" },
  { tag: "future-self", label: "Future Self" },
  { tag: "recovery-progress", label: "Recovery Progress" },
  { tag: "personal-growth", label: "Personal Growth" },
  { tag: "healthy-relationships", label: "Healthy Relationships" },
  { tag: "reconnecting", label: "Reconnecting With Yourself" },
];

export const TAG_LABELS: Record<string, string> = Object.fromEntries(
  PROMPT_TAG_CATEGORIES.map((c) => [c.tag, c.label]),
);

export function tagLabel(tag: string): string {
  return TAG_LABELS[tag] ?? tag.replace(/-/g, " ");
}

/* ------------------------------------------------------------------ */
/* Feelings & focus areas                                              */
/* ------------------------------------------------------------------ */

export interface FeelingOption {
  id: string;
  emoji: string;
  label: string;
  tags: string[];
}

export const FEELING_OPTIONS: FeelingOption[] = [
  { id: "anxious", emoji: "😰", label: "Anxious", tags: ["anxiety", "overthinking", "nervous-system", "fear"] },
  { id: "angry", emoji: "😡", label: "Angry", tags: ["anger", "resentment", "boundaries"] },
  { id: "sad", emoji: "😢", label: "Sad", tags: ["sadness", "grief", "loneliness"] },
  { id: "numb", emoji: "😶", label: "Numb", tags: ["numbness", "dissociation", "body-awareness"] },
  { id: "overwhelmed", emoji: "😵‍💫", label: "Overwhelmed", tags: ["overwhelm", "emotional-regulation", "nervous-system"] },
  { id: "heartbroken", emoji: "💔", label: "Heartbroken", tags: ["breakups", "grief", "letting-go", "trauma-bonds"] },
  { id: "ashamed", emoji: "😔", label: "Ashamed", tags: ["shame", "guilt", "inner-critic", "self-compassion"] },
  { id: "disconnected", emoji: "🫥", label: "Disconnected", tags: ["dissociation", "reconnecting", "identity"] },
  { id: "triggered", emoji: "😨", label: "Triggered", tags: ["triggers", "emotional-flashbacks", "hypervigilance", "safety"] },
  { id: "lonely", emoji: "🥺", label: "Lonely", tags: ["loneliness", "attachment", "relationships"] },
  { id: "hopeful", emoji: "🌱", label: "Hopeful", tags: ["hope", "future-self", "recovery-progress", "joy"] },
  { id: "ready", emoji: "❤️", label: "Ready to Reflect", tags: ["personal-growth", "identity", "values", "reconnecting"] },
];

export interface FocusOption {
  id: string;
  label: string;
  tags: string[];
}

export const FOCUS_OPTIONS: FocusOption[] = [
  { id: "childhood-trauma", label: "Childhood Trauma", tags: ["childhood-trauma", "inner-child", "emotional-neglect"] },
  { id: "relationships", label: "Relationships", tags: ["relationships", "healthy-relationships", "attachment"] },
  { id: "trauma-bonds", label: "Trauma Bonds", tags: ["trauma-bonds", "relationships", "attachment"] },
  { id: "breakup-recovery", label: "Breakup Recovery", tags: ["breakups", "letting-go", "grief"] },
  { id: "attachment", label: "Attachment", tags: ["attachment", "abandonment", "trust"] },
  { id: "abandonment", label: "Abandonment", tags: ["abandonment", "attachment", "fear"] },
  { id: "boundaries", label: "Boundaries", tags: ["boundaries", "people-pleasing", "codependency"] },
  { id: "self-worth", label: "Self-Worth", tags: ["self-worth", "self-esteem", "limiting-beliefs"] },
  { id: "inner-child", label: "Inner Child", tags: ["inner-child", "childhood-trauma"] },
  { id: "family-trauma", label: "Family Trauma", tags: ["family-trauma", "emotional-neglect", "childhood-trauma"] },
  { id: "emotional-regulation", label: "Emotional Regulation", tags: ["emotional-regulation", "nervous-system"] },
  { id: "triggers", label: "Triggers", tags: ["triggers", "emotional-flashbacks"] },
  { id: "shame", label: "Shame", tags: ["shame", "guilt", "inner-critic"] },
  { id: "grief", label: "Grief", tags: ["grief", "sadness", "letting-go"] },
  { id: "identity", label: "Identity", tags: ["identity", "values", "reconnecting"] },
  { id: "people-pleasing", label: "People-Pleasing", tags: ["people-pleasing", "codependency", "boundaries"] },
  { id: "trust", label: "Trust", tags: ["trust", "betrayal", "self-trust"] },
  { id: "survival-mode", label: "Survival Mode", tags: ["survival-mode", "hypervigilance", "control"] },
  { id: "nervous-system", label: "Nervous System", tags: ["nervous-system", "body-awareness", "safety"] },
  { id: "personal-growth", label: "Personal Growth", tags: ["personal-growth", "future-self", "recovery-progress"] },
];

/** Quick chips at the top of the library. */
export const QUICK_CHIPS: { label: string; tag: string }[] = [
  { label: "Triggered", tag: "triggers" },
  { label: "Relationships", tag: "relationships" },
  { label: "Inner Child", tag: "inner-child" },
  { label: "Anxiety", tag: "anxiety" },
  { label: "Shame", tag: "shame" },
  { label: "Boundaries", tag: "boundaries" },
  { label: "Trauma Bonds", tag: "trauma-bonds" },
  { label: "Self-Worth", tag: "self-worth" },
  { label: "Grief", tag: "grief" },
  { label: "Survival Mode", tag: "survival-mode" },
];

/* ------------------------------------------------------------------ */
/* Search                                                              */
/* ------------------------------------------------------------------ */

/** Natural-language words members type → tags they most likely mean. */
const SYNONYMS: Record<string, string[]> = {
  ex: ["breakups", "letting-go", "attachment", "trauma-bonds", "betrayal"],
  exes: ["breakups", "letting-go", "trauma-bonds"],
  breakup: ["breakups", "letting-go", "grief"],
  "break up": ["breakups", "letting-go", "grief"],
  divorce: ["breakups", "grief", "letting-go", "family-trauma"],
  "narcissistic abuse": ["trauma-bonds", "betrayal", "relationships", "self-trust"],
  narcissist: ["trauma-bonds", "betrayal", "relationships", "boundaries"],
  narc: ["trauma-bonds", "betrayal", "boundaries"],
  gaslighting: ["self-trust", "betrayal", "trust"],
  "trauma bond": ["trauma-bonds", "attachment", "relationships"],
  abandonment: ["abandonment", "attachment", "fear", "childhood-trauma", "relationships", "triggers"],
  abandoned: ["abandonment", "attachment", "loneliness"],
  rejection: ["abandonment", "shame", "self-worth", "attachment"],
  mother: ["family-trauma", "childhood-trauma", "emotional-neglect", "inner-child"],
  mum: ["family-trauma", "childhood-trauma", "emotional-neglect"],
  mom: ["family-trauma", "childhood-trauma", "emotional-neglect"],
  father: ["family-trauma", "childhood-trauma", "emotional-neglect", "inner-child"],
  dad: ["family-trauma", "childhood-trauma", "emotional-neglect"],
  parents: ["family-trauma", "childhood-trauma", "emotional-neglect"],
  family: ["family-trauma", "childhood-trauma", "boundaries"],
  childhood: ["childhood-trauma", "inner-child", "emotional-neglect"],
  child: ["inner-child", "childhood-trauma"],
  kid: ["inner-child", "childhood-trauma"],
  anger: ["anger", "resentment"],
  angry: ["anger", "resentment"],
  rage: ["anger", "resentment", "nervous-system"],
  shame: ["shame", "guilt", "inner-critic"],
  guilt: ["guilt", "shame", "people-pleasing"],
  lonely: ["loneliness", "attachment", "relationships"],
  loneliness: ["loneliness", "attachment"],
  alone: ["loneliness", "abandonment"],
  triggered: ["triggers", "emotional-flashbacks", "hypervigilance"],
  trigger: ["triggers", "emotional-flashbacks"],
  flashback: ["emotional-flashbacks", "triggers", "cptsd"],
  anxiety: ["anxiety", "overthinking", "nervous-system"],
  anxious: ["anxiety", "overthinking", "fear"],
  panic: ["anxiety", "nervous-system", "fight-flight-freeze-fawn"],
  overthinking: ["overthinking", "anxiety", "control"],
  rumination: ["overthinking", "anxiety"],
  boundaries: ["boundaries", "people-pleasing", "codependency"],
  boundary: ["boundaries", "people-pleasing"],
  "people pleasing": ["people-pleasing", "boundaries", "codependency"],
  "people pleaser": ["people-pleasing", "boundaries"],
  fawn: ["fight-flight-freeze-fawn", "people-pleasing"],
  trust: ["trust", "self-trust", "betrayal"],
  betrayal: ["betrayal", "trust", "relationships"],
  cheating: ["betrayal", "trust", "relationships"],
  grief: ["grief", "sadness", "letting-go"],
  loss: ["grief", "letting-go", "sadness"],
  death: ["grief", "sadness"],
  "self worth": ["self-worth", "self-esteem", "limiting-beliefs"],
  selfworth: ["self-worth", "self-esteem"],
  worth: ["self-worth", "self-esteem"],
  confidence: ["self-esteem", "self-worth", "self-trust"],
  numb: ["numbness", "dissociation"],
  dissociation: ["dissociation", "numbness", "body-awareness"],
  freeze: ["fight-flight-freeze-fawn", "dissociation", "numbness"],
  body: ["body-awareness", "nervous-system", "reconnecting"],
  sleep: ["nervous-system", "safety", "overwhelm"],
  stress: ["overwhelm", "nervous-system", "anxiety"],
  burnout: ["overwhelm", "survival-mode", "needs"],
  work: ["overwhelm", "boundaries", "perfectionism"],
  money: ["anxiety", "control", "safety"],
  perfectionism: ["perfectionism", "inner-critic", "control"],
  control: ["control", "anxiety", "safety"],
  hope: ["hope", "future-self", "recovery-progress"],
  gratitude: ["gratitude", "joy"],
  joy: ["joy", "gratitude", "reconnecting"],
  cptsd: ["cptsd", "trauma-recovery", "emotional-flashbacks"],
  ptsd: ["cptsd", "trauma-recovery", "triggers"],
  "inner critic": ["inner-critic", "shame", "self-compassion"],
  forgiveness: ["forgiveness", "letting-go", "resentment"],
  attachment: ["attachment", "abandonment", "relationships"],
  codependency: ["codependency", "people-pleasing", "boundaries"],
  safety: ["safety", "nervous-system", "hypervigilance"],
};

function normalise(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9\s'-]/g, " ").replace(/\s+/g, " ").trim();
}

/** Expand a free-text query into tags implied by synonyms. */
export function tagsForQuery(query: string): string[] {
  const q = normalise(query);
  if (!q) return [];
  const out = new Set<string>();
  for (const [key, tags] of Object.entries(SYNONYMS)) {
    if (q.includes(key) || key.includes(q)) tags.forEach((t) => out.add(t));
  }
  for (const c of PROMPT_TAG_CATEGORIES) {
    if (normalise(c.label).includes(q) || c.tag.includes(q)) out.add(c.tag);
  }
  return [...out];
}

export interface SearchArgs {
  query?: string;
  tags?: string[];
  depths?: PromptDepth[];
}

/** Ranked search over prompt text, tags, categories and keyword synonyms. */
export function searchPrompts({ query = "", tags = [], depths = [] }: SearchArgs): LibraryPrompt[] {
  const q = normalise(query);
  const words = q ? q.split(" ").filter((w) => w.length > 1) : [];
  const implied = q ? tagsForQuery(q) : [];

  const scored: { p: LibraryPrompt; score: number }[] = [];
  for (const p of LIBRARY_PROMPTS) {
    if (depths.length && !depths.includes(p.depth)) continue;
    if (tags.length && !tags.some((t) => p.tags.includes(t))) continue;

    let score = tags.length ? 5 : 1;
    if (q) {
      const text = normalise(p.text);
      let hit = 0;
      if (text.includes(q)) hit += 12;
      for (const w of words) if (text.includes(w)) hit += 4;
      for (const t of p.tags) {
        if (implied.includes(t)) hit += 6;
        if (t.includes(q) || normalise(tagLabel(t)).includes(q)) hit += 5;
      }
      if (hit === 0) continue;
      score += hit;
    }
    scored.push({ p, score });
  }

  scored.sort((a, b) => b.score - a.score || a.p.id.localeCompare(b.p.id));
  return scored.map((s) => s.p);
}

export function randomPrompt(pool: LibraryPrompt[] = LIBRARY_PROMPTS, exclude?: string): LibraryPrompt {
  const options = pool.length ? pool.filter((p) => p.id !== exclude) : LIBRARY_PROMPTS;
  const list = options.length ? options : LIBRARY_PROMPTS;
  return list[Math.floor(Math.random() * list.length)];
}

export function gentlerAlternative(prompt: LibraryPrompt): LibraryPrompt {
  const gentle = LIBRARY_PROMPTS.filter(
    (p) => p.depth === "gentle" && p.id !== prompt.id && p.tags.some((t) => prompt.tags.includes(t)),
  );
  if (gentle.length) return gentle[Math.floor(Math.random() * gentle.length)];
  const anyGentle = LIBRARY_PROMPTS.filter((p) => p.depth === "gentle");
  return anyGentle[Math.floor(Math.random() * anyGentle.length)];
}

export function promptByText(text: string): LibraryPrompt | undefined {
  const t = normalise(text);
  return LIBRARY_PROMPTS.find((p) => normalise(p.text) === t);
}

export const LIBRARY_PROMPT_COUNT = LIBRARY_PROMPTS.length;
